import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  clearAuditTrail,
  fetchApplicationAuditTrail,
  downloadApplicationAuditTrailPdf,
} from "../../../../store/reducers/applicationSlice";
import { notifyApiError, notifyApiSuccess } from "../../../../helpers/apiNotify";
import {
  formatStatusInText,
  formatStatusLabel,
} from "../statusConfirmDialog/statusConfirmDialog";
import "./AuditTrailModal.css";

const HIDDEN_ACTIONS = new Set(["VIEWED", "ACCESS_DENIED", "PDF_GENERATED"]);

const ACTION_LABELS = {
  CREATED: "Created",
  UPDATED: "Updated",
  STATE_CHANGED: "Status Changed",
  DISCREPANCY_UPDATED: "Discrepancy Updated",
  FILES_UPLOADED: "Files Uploaded",
  FILES_REMOVED: "Files Removed",
  CERTIFICATE_ISSUED: "Certificate Issued",
  CERTIFICATE_UPDATED: "Certificate Updated",
  CERTIFICATE_DOWNLOADED: "Certificate Downloaded",
  FILES_DOWNLOADED: "Files Downloaded",
  RENEWAL_REQUESTED: "Renewal Requested",
  RENEWAL_REMINDER_SENT: "Renewal Reminder",
  DELETED: "Deleted",
};

const AuditTrailModal = ({
  open,
  onClose,
  applicationId,
  accessToken,
  applicationNo,
  submittedBy,
  submittedFor,
}) => {
  const dispatch = useDispatch();
  const { logs, loading, error, pagination } = useSelector(
    (state) => state.applications.auditTrail
  );
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const [downloading, setDownloading] = useState(false);

  const displayAppNo =
    applicationNo ||
    (applicationId != null && applicationId !== ""
      ? `GB-${applicationId}`
      : "—");
  const displaySubmittedBy = submittedBy || "—";
  const displaySubmittedFor = submittedFor || "—";

  useEffect(() => {
    if (!open || !applicationId || !accessToken) return;
    dispatch(
      fetchApplicationAuditTrail({
        accessToken,
        id: applicationId,
        page,
        limit: 10,
        action: actionFilter || undefined,
      })
    );
  }, [open, applicationId, accessToken, page, actionFilter, dispatch]);

  useEffect(() => {
    if (!open) {
      setPage(1);
      setActionFilter("");
      setDownloading(false);
      dispatch(clearAuditTrail());
    }
  }, [open, dispatch]);

  const handleDownloadPdf = async () => {
    if (!applicationId || !accessToken || downloading) return;
    setDownloading(true);
    try {
      await downloadApplicationAuditTrailPdf({
        id: applicationId,
        accessToken,
        fileName: `audit-trail-${displayAppNo}`,
      });
      notifyApiSuccess("Audit trail exported");
    } catch (err) {
      notifyApiError(err, "Failed to download audit trail export");
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  const visibleLogs = (logs || []).filter(
    (log) => !HIDDEN_ACTIONS.has(log.action)
  );

  return (
    <div className="audit-modal-overlay" onClick={onClose}>
      <div
        className="audit-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="audit-modal-header">
          <div className="audit-modal-header-row">
            <h2>Application Audit Trail</h2>
            <div className="audit-modal-header-actions">
              <button
                type="button"
                className="audit-download-btn"
                onClick={handleDownloadPdf}
                disabled={downloading || !applicationId}
              >
                {downloading ? "Downloading…" : "Download"}
              </button>
              <button
                type="button"
                className="audit-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
          <div className="audit-modal-meta">
            <span>
              <strong>Application No:</strong> {displayAppNo}
            </span>
            <span>
              <strong>Submitted By:</strong> {displaySubmittedBy}
            </span>
            <span>
              <strong>Submitted For:</strong> {displaySubmittedFor}
            </span>
          </div>
        </div>

        <div className="audit-modal-filters">
          <label>
            Filter by action
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All actions</option>
              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="audit-modal-body">
          {loading ? (
            <div className="audit-modal-loading">Loading audit trail...</div>
          ) : error ? (
            <div className="audit-modal-error">{error}</div>
          ) : visibleLogs.length === 0 ? (
            <div className="audit-modal-empty">No audit entries found.</div>
          ) : (
            <ul className="audit-timeline">
              {visibleLogs.map((log) => (
                <li key={log.id} className="audit-timeline-item">
                  <div className="audit-timeline-title-row">
                    <p className="audit-timeline-desc">
                      {formatStatusInText(log.description || "—")}
                    </p>
                    <div className="audit-timeline-badge">
                      {log.action === "UPDATED" && log.remarks
                        ? "Remarks Updated"
                        : ACTION_LABELS[log.action] || log.action}
                    </div>
                  </div>
                  <div className="audit-timeline-main">
                    <div className="audit-timeline-meta">
                      <span className="audit-timeline-actor flex-row d-flex align-items-center gap-2">
                        <span>
                          {log.performed_by_name ||
                            (log.actor_type === "SYSTEM" ? "System" : "Unknown")}
                          {log.performed_by_role
                            ? ` (${log.performed_by_role})`
                            : ""}
                        </span>
                        {log.performed_by_email ? (
                          <span className="audit-timeline-email">
                            {log.performed_by_email}
                          </span>
                        ) : null}
                      </span>
                      <span>
                        {log.created_at
                          ? moment(log.created_at).format("YYYY-MM-DD HH:mm")
                          : "—"}
                      </span>
                    </div>
                    {log.remarks ? (
                      <p className="audit-timeline-remarks">
                        Remarks: {log.remarks}
                      </p>
                    ) : null}
                    {log.from_state_name || log.to_state_name ? (
                      <p className="audit-timeline-states">
                        {formatStatusLabel(log.from_state_name)} →{" "}
                        {formatStatusLabel(log.to_state_name)}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="audit-modal-footer">
          <button
            type="button"
            className="audit-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {pagination.currentPage || page} of{" "}
            {pagination.totalPages || 1}
          </span>
          <button
            type="button"
            className="audit-page-btn"
            disabled={
              loading ||
              (pagination.totalPages || 1) <= (pagination.currentPage || page)
            }
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailModal;
